"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck, HardHat, Layers, Building2, Construction, CircleCheck as CheckCircle2, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const specialistSolutions = [{
  title: "Access towers & stair towers",
  description: "Freestanding and tied stair systems designed for safe vertical transit on tight commercial footprints and public-facing sites.",
  tag: "Vertical Access"
}, {
  title: "Birdcage & internal scaffolds",
  description: "Continuous working platforms for high-ceiling work, atrium maintenance, internal plastering, and commercial fit-outs.",
  tag: "Internal Work"
}, {
  title: "Loading bays & cantilever systems",
  description: "Reinforced heavy-duty deck points and cantilevered structures allowing materials to land safely where ground space is restricted.",
  tag: "Material Handling"
}, {
  title: "Crash decks & safety netting",
  description: "Fall-mitigation boarding and tensioned safety decks protecting trades working below open roofs, joists, and floor voids.",
  tag: "Fall Protection"
}, {
  title: "Temporary public & site walkways",
  description: "Covered pedestrian gantries and secure walkways maintaining clear public thoroughfares and site logistics during building work.",
  tag: "Pedestrian Routing"
}, {
  title: "Bridging & beam structures",
  description: "Unit beam spans across fragile roofs, glass canopies, and narrow passages without placing load on delicate substrates.",
  tag: "Span Solutions"
}];
const targetSectors = [{
  role: "Main contractors",
  note: "Coordinated project schedules, full RAMS, and site-ready handovers."
}, {
  role: "Commercial developers",
  note: "Scalable access solutions phased across active construction programmes."
}, {
  role: "Local councils & authorities",
  note: "Public protection, licensed pavement gantries, and clear compliance logs."
}, {
  role: "Facilities & estate managers",
  note: "Discreet access for planned maintenance, plant replacements, and inspections."
}];
const keyAccreditations = ["NASC Registered", "CHAS Accredited"];
const sitePhotosQuery = new WP_Query({
  post_type: "site_photo",
  per_page: 20
});
function SpecialistAccessImageConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="w-full h-full bg-muted animate-pulse" />;
  }
  const photo = posts.find(p => p.title?.toLowerCase() === "specialist access scaffolding image" || p.customFields?.photo_name?.toLowerCase() === "specialist access scaffolding image");
  const imgSrc = photo?.customFields?.image || photo?.featuredImage;
  const imgAlt = photo?.customFields?.alt_text || photo?.title || "Specialist access scaffolding structure erected on an active site";
  if (imgSrc) {
    return <img src={imgSrc} data-wvc-srcset="" data-wvc-sizes="" alt={imgAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" />;
  }
  return <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/specialist_access_tower_scaffolding.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/specialist_access_tower_scaffolding.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/specialist_access_tower_scaffolding.png-wvc-sizes" alt="Specialist access scaffolding structure erected on an active site" loading="lazy" decoding="async" className="w-full h-full object-cover" />;
}
export default function SpecialistAccess() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="specialistaccessoverview" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Top Header & Answer-First Intro */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-medium">
                Specialist Scaffolding Services
              </Badge>
              <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                South East &amp; Home Counties
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
              Specialist access scaffolding
            </h1>

            <p className="text-lg text-muted-foreground leading-relaxed">
              When standard tube-and-fitting or independent scaffolding cannot meet site constraints, BMF Scaffolding provides bespoke, engineered access solutions. From complex birdcages and high-capacity loading bays to crash decks and public walkway gantries, we design and erect specialist systems tailored to challenging commercial environments across Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the South East.
            </p>

            {/* Quick Sector Tags */}
            <div className="pt-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-3">
                Supporting key delivery partners:
              </span>
              <div className="flex flex-wrap gap-2">
                {targetSectors.map((sector, i) => <div key={sector.role} data-index={i} className="inline-flex items-center gap-1.5 bg-muted px-3 py-1.5 rounded-md text-xs font-medium text-foreground border border-border">
                    <Building2 className="size-3.5 text-primary" />
                    <span>{sector.role}</span>
                  </div>)}
              </div>
            </div>

            <div className="flex flex-wrap gap-4 pt-2">
              <Button asChild size="lg">
                <Link to="/ContactRequestQuotePage">
                  <span>Discuss specialist access</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/ProjectsCaseStudiesPage">
                  <span>View project case studies</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Side Hero Overview Card with Image */}
          <div className="lg:col-span-5">
            <Card className="overflow-hidden border border-border shadow-md bg-card">
              <div className="relative aspect-4/3 w-full bg-muted overflow-hidden">
                <PostsProvider wp_query={{
                post_type: "site_photo",
                per_page: 20,
                status: ["publish"]
              }}>
                  <SpecialistAccessImageConsumer />
                </PostsProvider>
                <div className="absolute top-3 left-3 bg-secondary text-secondary-foreground px-3 py-1 rounded-md text-xs font-medium shadow-xs">
                  Site-Specific Planning
                </div>
              </div>

              <CardHeader className="pb-3">
                <CardTitle className="text-xl font-bold font-default">
                  Engineered for non-standard sites
                </CardTitle>
                <CardDescription>
                  Detailed calculations, method statements, and handovers for sensitive or restricted footprints.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-3 pt-0">
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Full RAMS and load-rating verification</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Licensed street work and pavement gantry management</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>Supervised by 20+ years of industry experience</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Separator className="my-14" />

        {/* Section: Specialist Configurations Grid */}
        <div className="space-y-8">
          <div className="max-w-3xl space-y-2">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight font-default">
              Bespoke access configurations
            </h2>
            <p className="text-muted-foreground text-base">
              Non-standard builds tailored to internal fit-outs, heritage properties, high-traffic corridors, and heavy material transit.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {specialistSolutions.map((solution, i) => <motion.div key={solution.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border shadow-xs hover:shadow-md transition-[box-shadow,border-color] bg-card flex flex-col justify-between">
                  <CardHeader className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="p-2 rounded-md bg-muted text-primary">
                        <Layers className="size-5" />
                      </div>
                      <Badge variant="outline" className="text-xs font-normal">
                        {solution.tag}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg font-semibold font-default">
                      {solution.title}
                    </CardTitle>
                    <CardDescription className="text-sm leading-relaxed">
                      {solution.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0 pb-6">
                    <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
                      <span>Enquire about this build</span>
                      <ArrowRight className="size-3" />
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>

        {/* Section: How BMF Delivers Specialist Scaffolding */}
        <div className="mt-16 bg-muted/60 rounded-xl p-8 sm:p-10 border border-border">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-4">
              <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
                <HardHat className="size-4" />
                <span>Operational standards &amp; compliance</span>
              </div>
              <h3 className="text-2xl font-bold tracking-tight font-default">
                Safety-led erection and site-specific planning
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                Specialist access projects require exact planning to prevent operational downtime and safeguard trades. BMF Scaffolding operates under Pete Wood's 20+ years of hands-on industry expertise, backed by comprehensive risk assessments, weekly TG20 inspections, and strict scaffold tagging. Every structure is handed over with formal handover certificates and clear load-rating notices.
              </p>

              {/* Accreditations Row */}
              <div className="pt-2 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <ShieldCheck className="size-4 text-primary" />
                  <span>Confirmed accreditations:</span>
                </div>
                {keyAccreditations.map((acc, i) => <Badge key={acc} data-index={i} variant="secondary" className="text-xs px-2.5 py-1 font-medium bg-card text-card-foreground border border-border">
                    {acc}
                  </Badge>)}
              </div>
            </div>

            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-center">
              <Card className="border border-border bg-card p-5">
                <div className="flex items-start gap-3">
                  <div className="p-2.5 rounded-md bg-primary/10 text-primary shrink-0">
                    <PhoneCall className="size-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold font-default">Need technical advice?</h4>
                    <p className="text-xs text-muted-foreground mt-0.5 mb-3">
                      Speak with our operational team for initial site feasibility.
                    </p>
                    <Button asChild size="sm" className="w-full">
                      <Link to="/ContactRequestQuotePage">
                        <span>Request site survey</span>
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>

              <Card className="border border-border bg-card p-5">
                <div className="flex items-start gap-3">
                  <div className="p-2.5 rounded-md bg-secondary text-secondary-foreground shrink-0">
                    <Construction className="size-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold font-default">Coverage map</h4>
                    <p className="text-xs text-muted-foreground mt-0.5 mb-3">
                      Serving Berkshire, Surrey, Hampshire, Bucks &amp; London.
                    </p>
                    <Button asChild variant="outline" size="sm" className="w-full">
                      <Link to="/AreasWeCoverPage">
                        <span>Check coverage areas</span>
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}